import { useMemo } from 'react';

import { getImageUrl } from '@zola-helpers/client/dist/es/crossSell';
import {
  getVendorWordCategory,
  getVendorWordPlural,
  getVendorWordSingular,
} from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { SeoSrpMetricDataView } from '@zola/svc-marketplace-ts-types';

import VendorHead from '~/components/common/layouts/head/VendorHead';
import {
  SearchResultPageSearchResult,
  useSearchResultsPageContext,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { isSearchLocation } from '~/util/searchUtils';

import { isQuizCardView } from '../../../VendorCardGrid/utils';
import { VendorSearchRequest } from '../../types/types';
import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';

const getLocationSuffix = (selectedLocation?: LocationOrAllMarketsType | null) => {
  if (isSearchLocation(selectedLocation)) {
    return ` in ${selectedLocation.label}`;
  }
  return '';
};

export const getPageTitle = (
  vendorSearch?:
    | Pick<VendorSearchRequest, 'selectedLocation' | 'selectedTaxonomy' | 'selectedFacets'>
    | Record<string, never>
) => {
  if (!vendorSearch) return undefined;
  const { selectedLocation, selectedTaxonomy, selectedFacets } = vendorSearch;

  const category = getVendorWordCategory(selectedTaxonomy?.key);
  const locationSuffix = getLocationSuffix(selectedLocation);
  const facetDescription = getCanonicalFacetDescription(selectedTaxonomy?.key, selectedFacets);

  const description = `Wedding ${category}${locationSuffix} - Zola`;
  if (facetDescription) {
    return `${facetDescription} ${description}`;
  }
  return description;
};

export const getPageDescription = (
  vendorSearch?: Pick<
    VendorSearchRequest,
    'selectedLocation' | 'selectedTaxonomy' | 'selectedFacets'
  >
) => {
  if (!vendorSearch) return undefined;
  const { selectedLocation, selectedTaxonomy, selectedFacets } = vendorSearch;
  const categoryPlural = getVendorWordPlural(selectedTaxonomy?.key);
  const categorySingular = getVendorWordSingular(selectedTaxonomy?.key);
  const locationSuffix = getLocationSuffix(selectedLocation);
  const facetDescription = getCanonicalFacetDescription(
    selectedTaxonomy?.key,
    selectedFacets
  )?.toLocaleLowerCase();

  const formattedFacetDescription = facetDescription ? `${facetDescription} ` : '';

  let description = `Find your perfect ${formattedFacetDescription}wedding ${categoryPlural}${locationSuffix} at Zola. `;
  description += `We'll help you narrow down your ${categorySingular} search in minutes.`;
  return description;
};

// This will evolve over time as we address our canonical and indexing SEO issues
const isNoIndex = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult,
  canonicalUrl: string,
  seoCanonicalUrl: string,
  seoMetrics: SeoSrpMetricDataView | null
) => {
  const { page } = vendorSearch || {};

  // We don't want to index pages beyond the first page but it will still allow google to follow links on the page
  if (page && page > 1) return true;

  // No vendors = no index
  if (searchResults?.totalHits === 0) return true;

  // If an SRP does not self canonicalize, add a noindex.
  if (seoCanonicalUrl !== canonicalUrl) return true;

  // If seoMetrics are available, we can make a decision based on the data
  // If not available, err on the side of caution and index it
  if (seoMetrics !== null) {
    // If all 3 stats are 0, noindex
    if (
      seoMetrics.storefrontCount === 0 &&
      seoMetrics.totalViewCount === 0 &&
      seoMetrics.uniqueUserCount === 0
    ) {
      return true;
    }
  }

  // Let's index this SRP
  return false;
};

/**
 * Injects the SEO/meta tags into the search results page
 *
 * @returns
 */
export const SearchResultsHead = ({ isPreAuthSrp = false }: { isPreAuthSrp?: boolean }) => {
  const { vendorSearch, canonicalUrl, seoCanonicalUrl, searchResults, seoMetrics } =
    useSearchResultsPageContext();

  const { totalHits } = searchResults;

  const pageTitle = useMemo(() => {
    const baseTitle = getPageTitle(vendorSearch);
    if (isPreAuthSrp) {
      const number = Math.min(Math.max(3, totalHits), 40);
      return `Top ${number} ${baseTitle}`;
    }
    return baseTitle;
  }, [isPreAuthSrp, totalHits, vendorSearch]);

  const firstVendor = searchResults.vendors[0];
  let imageUrl = undefined;
  if (firstVendor && !isQuizCardView(firstVendor)) {
    const imageIds = firstVendor.imageIds;
    if (imageIds && imageIds.length > 0) {
      // Resize so its always a 4:3 (we don't enforce aspect ratios on upload)
      imageUrl = getImageUrl(imageIds[0]) + '?w=400&h=300&fit=crop&q=60&fm=webp';
    }
  }

  return (
    <VendorHead
      title={pageTitle}
      description={getPageDescription(vendorSearch) || ''}
      canonicalUrl={seoCanonicalUrl}
      imageUrl={imageUrl}
      noindex={isNoIndex(vendorSearch, searchResults, canonicalUrl, seoCanonicalUrl, seoMetrics)}
    />
  );
};
